// =============================================================================
// IntegrationRotation — bloc de rotation d'un secret d'intégration (D15).
// Réutilisable (Zoho / Pennylane). Appelle POST /api/integrations/rotate qui
// écrit dans l'AUTORITÉ (collection integrations), jamais integration_connections.
// Fail-closed, secret write-once jamais réaffiché, empreintes avant/après only.
// =============================================================================
import { useState } from "react";
import axios from "axios";
import { Key, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const IntegrationRotation = ({ integrationType = "zoho_workdrive", ficheId = null, onSuccess }) => {
  const [newSecret, setNewSecret] = useState("");
  const [oauthCode, setOauthCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const isZoho = integrationType === "zoho_workdrive";

  const handleRotate = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/api/integrations/rotate`,
        {
          // Ciblage EXPLICITE de la fiche autorité par _id (section Autorité) ;
          // integration_type = fallback legacy uniquement si pas de ficheId.
          fiche_id: ficheId || null,
          integration_type: ficheId ? null : integrationType,
          new_secret: newSecret.trim() || null,
          oauth_code: isZoho ? (oauthCode.trim() || null) : null,
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setResult(res.data);
      if (res.data.success) {
        setNewSecret("");
        setOauthCode("");
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = isZoho ? oauthCode.trim().length > 0 : newSecret.trim().length > 0;

  return (
    <div
      data-testid="integration-rotation"
      style={{ marginTop: 16, padding: 16, borderRadius: 12, background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color: "var(--oh-text)", fontWeight: 500 }}>
        <Key className="w-4 h-4" />
        <span>Rotation du secret (autorité)</span>
      </div>
      <p style={{ fontSize: 13, color: "var(--oh-text-secondary)", marginBottom: 12, lineHeight: 1.6 }}>
        Écrit dans l'autorité, jamais dans la copie. En cas d'échec de l'échange, aucune écriture. Le secret n'est jamais réaffiché ; seules les empreintes avant/après sont montrées.
      </p>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gap: 4 }}>
          <Label htmlFor="rot-secret" style={{ color: "var(--oh-text-secondary)" }}>
            {isZoho ? "Nouveau client secret (laisser vide pour conserver l'actuel)" : "Nouveau api_token"}
          </Label>
          <Input
            id="rot-secret"
            type="password"
            autoComplete="new-password"
            value={newSecret}
            onChange={(e) => setNewSecret(e.target.value)}
            placeholder={isZoho ? "•••••••• (write-once)" : "nouveau token Pennylane"}
            data-testid="rotation-new-secret"
          />
        </div>
        {isZoho && (
          <div style={{ display: "grid", gap: 4 }}>
            <Label htmlFor="rot-code" style={{ color: "var(--oh-text-secondary)" }}>Code d'autorisation OAuth</Label>
            <Input
              id="rot-code"
              value={oauthCode}
              onChange={(e) => setOauthCode(e.target.value)}
              placeholder="1000.xxxx.xxxx (expire en 3 min)"
              data-testid="rotation-oauth-code"
            />
          </div>
        )}
        <Button
          onClick={handleRotate}
          disabled={busy || !canSubmit}
          style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
          data-testid="rotation-submit"
          aria-label="Effectuer la rotation du secret"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Effectuer la rotation"}
        </Button>
      </div>
      {result && (
        <div
          data-testid="rotation-result"
          style={{
            marginTop: 12, padding: 12, borderRadius: 8,
            background: result.success ? "var(--oh-stat-green-bg)" : "var(--oh-stat-red-bg)",
            color: result.success ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)",
          }}
        >
          <p style={{ fontSize: 13, fontWeight: 500 }}>
            {result.success ? "Rotation effectuée." : `Échec : ${result.error || "rotation impossible"}`}
          </p>
          {(result.before_fingerprint || result.after_fingerprint) && (
            <p style={{ fontSize: 12, marginTop: 4, fontFamily: "monospace", opacity: 0.9 }}>
              empreinte avant {result.before_fingerprint || "∅"} → après {result.after_fingerprint || "∅"}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default IntegrationRotation;
